import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Laptop, Calendar } from 'lucide-react';

const Home = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero - Manifesto (full-viewport) */}
      <section className="bg-cream min-h-screen flex flex-col justify-center px-4 sm:px-6 pt-28 sm:pt-32 md:pt-36 pb-12 sm:pb-16 md:pb-20 relative">
        <div className="max-w-7xl mx-auto w-full fade-in-section" ref={heroRef}>
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8 md:mb-12">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-dark-green/40 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-dark-green" />
            </span>
            <p className="text-dark-green/60 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase">
              Dharma Initiative &middot; Applications open
            </p>
          </div>

          {/* Massive serif manifesto */}
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-tight tracking-tight mb-10 md:mb-14 max-w-6xl">
            <span className="text-gradient-hero">Tomorrow's leaders,</span>
            <br />
            <span className="text-gradient-hero-light italic">created by today's.</span>
          </h1>

          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <p className="text-dark-green/75 text-lg sm:text-xl md:text-2xl leading-snug max-w-2xl">
                An NGO community nurturing the next generation of leaders, and creating spaces for growth and innovation.
              </p>
            </div>
          </div>
        </div>

      </section>

      {/* Events Strip */}
      <section className="bg-cream border-y border-dark-green/15 py-6 sm:py-8 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-baseline justify-between gap-4 mb-3 sm:mb-4">
            <p className="text-dark-green/60 font-mono tracking-[0.25em] text-[10px] sm:text-xs uppercase">
              Our events
            </p>
            <Link
              to="/events"
              data-testid="events-strip-cta"
              className="inline-flex items-center gap-1.5 font-mono tracking-[0.2em] uppercase text-[10px] sm:text-xs text-dark-green hover:text-dark-green/70 transition-colors group"
            >
              See all events
              <span aria-hidden className="group-hover:translate-x-0.5 transition-transform">&rarr;</span>
            </Link>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-6 sm:gap-x-10 md:gap-x-14 gap-y-3 font-serif text-dark-green text-xl sm:text-2xl md:text-3xl lg:text-4xl">
            <span>Conversation @ Goldman Sachs</span>
            <span className="text-dark-green/25">/</span>
            <span>StartUp101 @ WeWork Soho</span>
            <span className="text-dark-green/25">/</span>
            <span>Round Table Dinner @ One Aldwych</span>
            <span className="text-dark-green/25">/</span>
            <span>
              Law Event @ Goodwin
              <span className="ml-2 align-middle text-dark-green/50 font-mono uppercase tracking-widest text-[10px] sm:text-xs">
                Incoming
              </span>
            </span>
          </div>
        </div>
      </section>

      {/* Launch Event Video Section - cinematic editorial */}
      <LaunchHighlights />

      {/* How we connect - split feature blocks */}
      <section className="bg-cream py-14 sm:py-20 md:py-24 border-t border-dark-green/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-10 md:mb-14 fade-in-section">
            <p className="text-dark-green/60 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
              <span className="text-dark-green/40">&rarr;</span> 01 / How we connect
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-dark-green leading-tight max-w-3xl">
              Two channels.<span className="text-dark-green/45"> One mission.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-dark-green/15 border-y border-dark-green/15">
            {/* Online */}
            <div className="group hover:bg-dark-green/[0.02] transition-colors duration-300 flex flex-col">
              <div className="relative aspect-[16/10] overflow-hidden border-b border-dark-green/15 bg-dark-green/[0.04]" data-testid="connect-online-image">
                <img
                  src="https://framerusercontent.com/images/xPC2bQhWqtdgCq1Xpnu3ml8zbxo.jpg?width=2400&height=1600"
                  alt="Mentorship 1-1 coffee chat"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700 ease-out"
                />
              </div>
              <div className="p-6 sm:p-9 md:p-10 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-xs tracking-widest text-dark-green/50 pt-1">
                    /001 &middot; ONLINE
                  </span>
                  <Laptop className="w-5 h-5 text-dark-green/60" />
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl text-dark-green leading-[1.05] mb-5">
                  Mentorship,<br />in your pocket.
                </h3>
                <p className="text-dark-green/75 text-base md:text-lg leading-relaxed mb-6 max-w-md">
                  Our <span className="font-bold text-dark-green">app</span> connects young professionals and entrepreneurs with hyper-successful mentors through fluid, intentional virtual <span className="italic font-bold text-dark-green">"coffee-chats"</span>. It also offers webinars and an exclusive resource bank.
                </p>
                <ul className="flex flex-wrap gap-2">
                  {['1-1 Calls', 'Webinars', 'Resource bank', 'Internships'].map((tag) => (
                    <li
                      key={tag}
                      className="text-[11px] font-mono tracking-tight uppercase text-dark-green/75 border border-dark-green/20 rounded-full px-3 py-1.5"
                    >
                      {tag}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Events */}
            <div className="group hover:bg-dark-green/[0.02] transition-colors duration-300 flex flex-col">
              <div className="relative aspect-[16/10] overflow-hidden border-b border-dark-green/15 bg-dark-green/[0.04]" data-testid="connect-events-image">
                <img
                  src="https://customer-assets.emergentagent.com/job_dharma-refresh/artifacts/bi1ae8df_DSC04637.jpeg"
                  alt="Dharma Initiative launch event networking"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700 ease-out"
                />
              </div>
              <div className="p-6 sm:p-9 md:p-10 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-xs tracking-widest text-dark-green/50 pt-1">
                    /002 &middot; EVENTS
                  </span>
                  <Calendar className="w-5 h-5 text-dark-green/60" />
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl text-dark-green leading-[1.05] mb-5">
                  Gather,<br />in real life.
                </h3>
                <p className="text-dark-green/75 text-base md:text-lg leading-relaxed mb-6 max-w-md">
                  We host targeted, <span className="font-bold text-dark-green">small-group events</span>, and <span className="font-bold text-dark-green">community flagships</span>. Off the back of our 200-person Launch event, we've lined up events at some of the best places across London. Stay tuned!
                </p>
                <ul className="flex flex-wrap gap-2">
                  {['Goldman Sachs', 'WeWork Soho', 'One Aldwych', 'Goodwin', '+More'].map((tag) => (
                    <li
                      key={tag}
                      className="text-[11px] font-mono tracking-tight uppercase text-dark-green/75 border border-dark-green/20 rounded-full px-3 py-1.5"
                    >
                      {tag}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Voices - YC-style inline quotes with portrait signatures + hover card */}
      <VoicesSection />
    </div>
  );
};

// Plain YouTube embed — title + iframe, no overlay.
const LaunchHighlights = () => {
  const videoId = 'I17PGnsHit4';

  return (
    <section className="bg-dark-green py-10 sm:py-14 md:py-20 px-4 sm:px-6 border-t border-cream/10" data-testid="launch-video-section">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 md:mb-10">
          <p className="text-cream/50 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
            <span className="text-cream/30">&rarr;</span> Launch event
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-cream leading-tight max-w-3xl">
            Launch night,<span className="text-cream/45 italic"> in motion.</span>
          </h2>
        </div>
        <div className="aspect-video">
          <iframe
            title="Dharma Initiative Launch Event Highlights"
            data-testid="launch-video-iframe"
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
};

// Inline voices section: text-quotes-with-avatar-signatures on the left,
// big portrait card on the right that swaps as you hover each avatar.
const voices = [
  {
    quote:
      'A great platform to connect with an incredible network of talented individuals and participate in transformative opportunities.',
    name: 'Krishan Parmar',
    course: 'Economics, UCL',
    image: '/images/voices/krishan-parmar.png',
  },
  {
    quote:
      'An extraordinary opportunity to participate in innovative projects that foster leadership.',
    name: 'Rayna Chawla',
    course: 'PPE, Oxford',
    image: '/images/voices/rayna-chawla.png',
  },
  {
    quote:
      'The community encourages curiosity, critical thinking, and creativity.',
    name: 'Chinmay Mullapudi',
    course: 'Medicine, UCL',
    image: '/images/voices/chinmay-mullapudi.png',
  },
];

const VoicesSection = () => {
  const [activeIdx, setActiveIdx] = React.useState(null);
  const [popupStyle, setPopupStyle] = React.useState({ top: 0, left: 0, width: 280, arrowLeft: 0 });
  const avatarRefs = React.useRef([]);

  React.useLayoutEffect(() => {
    if (activeIdx === null) return;
    const avatar = avatarRefs.current[activeIdx];
    if (!avatar) return;

    const compute = () => {
      const rect = avatar.getBoundingClientRect();
      const vw = window.innerWidth;
      const margin = 16;
      // Responsive width — narrower on mobile.
      const width = vw < 640 ? 240 : vw < 1024 ? 280 : 352;
      const avatarCenter = rect.left + rect.width / 2;
      const desiredLeft = avatarCenter - width / 2;
      const left = Math.max(margin, Math.min(desiredLeft, vw - width - margin));
      const arrowLeft = Math.max(12, Math.min(avatarCenter - left, width - 12));
      setPopupStyle({
        top: rect.bottom + 12,
        left,
        width,
        arrowLeft,
      });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, { passive: true });
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute);
    };
  }, [activeIdx]);

  const active = activeIdx !== null ? voices[activeIdx] : null;

  return (
    <section className="bg-dark-green py-16 sm:py-20 md:py-28 px-4 sm:px-6 border-t border-dark-green overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12 md:mb-16 fade-in-section">
          <p className="text-cream/50 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
            <span className="text-cream/30">&rarr;</span> 03 / In our community's words
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-cream leading-tight max-w-3xl">
            From the people<span className="text-cream/45"> living it.</span>
          </h2>
          <p className="mt-4 text-cream/45 text-xs sm:text-sm font-mono tracking-[0.2em] uppercase hidden md:block">
            Hover over a face to meet them.
          </p>
        </div>

        {/* YC-style inline quotes with avatar signatures */}
        <p
          className="font-serif text-cream text-xl sm:text-2xl md:text-[1.85rem] lg:text-[2.1rem] leading-[1.55] tracking-tight max-w-5xl"
          data-testid="voices-inline-quotes"
        >
          {voices.map((v, i) => (
            <React.Fragment key={v.name}>
              <span
                className="relative whitespace-normal"
                style={{
                  backgroundImage:
                    'linear-gradient(transparent calc(100% - 2px), rgba(245, 243, 237, 0.18) calc(100% - 2px))',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '100% 100%',
                }}
              >
                &ldquo;{v.quote}&rdquo;
              </span>

              <span
                className="relative inline-block align-middle mx-2 sm:mx-3"
                onMouseEnter={() => setActiveIdx(i)}
                onMouseLeave={() => setActiveIdx(null)}
                onFocus={() => setActiveIdx(i)}
                onBlur={() => setActiveIdx(null)}
              >
                <button
                  ref={(el) => (avatarRefs.current[i] = el)}
                  type="button"
                  data-testid={`voice-avatar-${i}`}
                  aria-label={`${v.name}, ${v.course}`}
                  className="inline-flex focus:outline-none align-middle"
                >
                  <img
                    src={v.image}
                    alt={v.name}
                    className={`inline-block w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full object-cover bg-cream/5 ring-2 transition-all duration-300 ${
                      activeIdx === i
                        ? 'ring-cream/80 scale-110 grayscale-0'
                        : 'ring-cream/15 grayscale hover:ring-cream/50 hover:grayscale-0'
                    }`}
                  />
                </button>
              </span>

              {i < voices.length - 1 && <span>&nbsp;&nbsp;</span>}
            </React.Fragment>
          ))}
        </p>
      </div>

      {/* Shared popup — fixed positioned at viewport scope so it cannot be clipped by section overflow */}
      {active && (
        <div
          data-testid={`voice-popup-${activeIdx}`}
          className="pointer-events-none fixed z-50 transition-opacity duration-200 opacity-100"
          style={{
            top: `${popupStyle.top}px`,
            left: `${popupStyle.left}px`,
            width: `${popupStyle.width}px`,
          }}
        >
          <span
            className="absolute -top-[6px] w-3 h-3 rotate-45 bg-cream border-l border-t border-dark-green/15"
            style={{ left: `${popupStyle.arrowLeft - 6}px` }}
          />
          <div className="relative bg-cream text-dark-green border border-dark-green/15 p-4 sm:p-5 shadow-[0_24px_48px_-12px_rgba(17,48,38,0.45)] text-left">
            <div className="flex items-start gap-3 sm:gap-4">
              <img
                src={active.image}
                alt=""
                aria-hidden
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover flex-shrink-0 ring-1 ring-dark-green/20"
              />
              <div className="min-w-0 flex-1">
                <p className="font-serif text-base sm:text-xl leading-tight break-words">
                  {active.name}
                </p>
                <p className="mt-1 text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-dark-green/60 break-words">
                  {active.course}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Home;
